import { createSession, exchangeToken, SessionResponse, TokenResponse } from '../hooks/useAuth';

/**
 * Genera un code verifier para PKCE
 * @param length - Longitud del code verifier (default: 128)
 */
export const generateCodeVerifier = (length: number = 128): string => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  
  return result;
};

/**
 * Genera un code challenge SHA256 para PKCE
 * @param codeVerifier - El code verifier
 */
export const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(digest)));
  
  // Convertir a base64url
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

/**
 * Flujo completo de autenticación PKCE
 * @param embedId - El embed key/ID
 * @returns Promise con el accessToken
 */
export const authenticateWithPKCE = async (embedId: string): Promise<string> => {
  try {
    console.log('🔐 Iniciando autenticación PKCE...');
    
    // Paso 1: Generar code verifier y challenge
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    
    console.log('📝 Code verifier generado:', codeVerifier);
    console.log('🔑 Code challenge generado:', codeChallenge);
    
    // Paso 2: Crear sesión
    console.log('🚀 Creando sesión...');
    const sessionResponse: SessionResponse = await createSession(embedId, codeChallenge);
    console.log('✅ Sesión creada:', sessionResponse);
    
    // Paso 3: Intercambiar por token
    console.log('🔄 Intercambiando por token...');
    const tokenResponse: TokenResponse = await exchangeToken(sessionResponse.sessionId, codeVerifier);
    console.log('🎉 Token obtenido:', tokenResponse);
    
    if (!tokenResponse.ok) {
      throw new Error('Error al obtener el token de acceso');
    }
    
    return tokenResponse.accessToken;
  } catch (error) {
    console.error('❌ Error en autenticación PKCE:', error);
    throw error;
  }
};

/**
 * Valida si un token JWT está expirado
 * @param token - El JWT token
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('❌ Error validando token:', error);
    return true;
  }
};

/**
 * Obtiene la información del payload del JWT
 * @param token - El JWT token
 */
export const getTokenPayload = (token: string): any => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch (error) {
    console.error('❌ Error decodificando token:', error);
    return null;
  }
};
























